import React, { useState, useEffect, useRef } from "react";
import { 
  Table, 
  Button, 
  Drawer, 
  Form, 
  Input, 
  Select, 
  message, 
  Space, 
  Tooltip,
  InputNumber,
  Tag,
  Spin
} from "antd";
import axios from "axios";
import { 
  SearchOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined, 
  PlusOutlined 
} from "@ant-design/icons";
import constants from "../../constant";
import { useAuth } from "../../context/authContext";

const { Option } = Select;

const OrderManagement = () => {
  const {currentUser, roles} = useAuth();
  const isAdmin = roles.includes('ADMIN');
  const [orders, setOrders] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [systemUsers, setSystemUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 10,
      position: ["topCenter"],
    },
    filters: {},
  });
  const searchInput = useRef(null);
  const [form] = Form.useForm();

  // Fetch orders with filtering
  const fetchOrders = async () => {
    setLoading(true);
    try {
      let query = [];
      Object.keys(tableParams.filters).forEach((k) => {
        if (tableParams.filters[k]?.length > 0) {
          query.push(`${k}:sw=${tableParams.filters[k]}`);
        }
      });
      query = `page=${tableParams.pagination.current}&limit=${tableParams.pagination.pageSize}&firebaseId=${currentUser._delegate.uid}&${query.join("&")}`;
      const res = await axios.get(`${constants.DOMAIN}/api/v1/marketing/getOrderData?${query}&version=null`);
      setOrders(res.data.data.orders);
      setTableParams({
        ...tableParams,
        pagination: {
          ...tableParams.pagination,
          total: res.data.results,
        },
      })
    } catch (error) {
      console.error("Failed to fetch orders:", error);
      message.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  // Fetch system users for POC selection
  const fetchSystemUsers = async () => {
    try {
      const response = await axios.get(`${constants.DOMAIN}/api/v1/systemuser/getUser`);
      setSystemUsers(response.data.data.users);
    } catch (error) {
      message.error('Failed to fetch system users');
    }
  };

  useEffect(() => {
    fetchSystemUsers();
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [tableParams.filters]);

  // Update form when currentOrder changes
  useEffect(() => {
    if (currentOrder) {
      form.setFieldsValue({
        amount: currentOrder.amount,
        primaryPhone: currentOrder.primaryPhone,
        quantity: currentOrder.quantity,
        productCode: currentOrder.productCode,
        orderStatus: currentOrder.orderStatus,
        pincode: currentOrder.pincode,
        flat: currentOrder.flat,
        area: currentOrder.area,
        city: currentOrder.city,
        state: currentOrder.state,
        poc: currentOrder.poc,
        rate: currentOrder.rate,
        otherCharges: currentOrder.otherCharges,
        orderValueIncludingGST: currentOrder.orderValueIncludingGST,
      });
    } else {
      form.resetFields();
    }
  }, [currentOrder, form]);

  const handleEdit = (order) => {
    setCurrentOrder(order);
    setIsDrawerOpen(true);
  };

  const handleDelete = async (id) => {
    setLoading(true);
    try {
      await axios.delete(`${constants.DOMAIN}/api/v1/marketing/deleteOrder/${id}`);
      message.success("Order deleted successfully");
      fetchOrders();
    } catch (error) {
      console.error("Failed to delete order:", error);
      message.error("Failed to delete order");
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (values) => {
    setSubmitting(true);
    try {
      await axios.post(`${constants.DOMAIN}/api/v1/marketing/updateOrder/${currentOrder._id}`, values);
      message.success("Order updated successfully");
      setIsDrawerOpen(false);
      fetchOrders();
      form.resetFields();
      setCurrentOrder(null);
    } catch (error) {
      console.error("Full error:", error);
      message.error("Failed to save order");
    } finally {
      setSubmitting(false);
    }
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            size="small"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          >
            Search
          </Button>
          <Button size="small" onClick={() => clearFilters && handleReset(clearFilters)}>
            Reset
          </Button>
          <Button type="link" size="small" onClick={close}>
            Close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setTableParams({
      ...tableParams,
      pagination: { ...tableParams.pagination, current: 1 },
      filters: { [dataIndex]: selectedKeys },
    });
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    fetchOrders();
  };

  const orderStatusColors = {
    CREATED: 'blue',
    INPROGRSS: 'orange',
    COMPLETED: 'green',
    LOST: 'red',
  };

  const handleTableChange = (pagination, filters, sorter, extra) => {
    setTableParams({
      pagination,
      filters,
      sorter,
    })

    // `dataSource` is useless since `pageSize` changed
    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setOrders([])
    }
  }

  return (
    <div className="p-6">
      <Spin spinning={loading}>
        <Table
          dataSource={orders}
          rowKey="_id"
          // pagination={tableParams.pagination}
          columns={[
            isAdmin ? {
              title: "POC",
              dataIndex: "poc",
              filters: (systemUsers || []).map((d) => ({
                value: d._id,
                text: d.name,
              })),
              render: (data, record) => {
                return (
                  <Select
                    size="small"
                    defaultValue={systemUsers?.find(item => item._id.toString() === data?.toString())?.name}
                    style={{ width: '100%', minWidth: 100 }}
                    onChange={(e) => {
                      record.poc = e;
                    }}
                    options={(systemUsers || []).map((d) => ({
                      value: d._id,
                      label: d.name,
                    }))}
                  />
                );
              },
              responsive: ['md'],
            }:{
              width:0
            },
            { 
              title: "Product Code", 
              dataIndex: "productCode", 
              ...getColumnSearchProps("productCode") 
            },
            { 
              title: "Order Value", 
              dataIndex: "amount",
              render: (amount) => `₹${amount.toLocaleString('en-IN')}`
            },
            { 
              title: "Quantity", 
              dataIndex: "quantity" 
            },
            { 
              title: "Rate", 
              dataIndex: "rate",
              render: (amount) => `₹${amount.toLocaleString('en-IN')}`
            },
            { 
              title: "Other Charges", 
              dataIndex: "otherCharges",
              render: (amount) => `₹${amount.toLocaleString('en-IN')}`
            },
            { 
              title: "Final Amount ( GST )", 
              dataIndex: "orderValueIncludingGST",
              render: (amount) => `₹${amount["$numberDecimal"].toLocaleString('en-IN')}`
            },
            {
              title: "Status",
              dataIndex: "orderStatus",
              render: (status) => (
                <Tag color={orderStatusColors[status]}>
                  {status}
                </Tag>
              )
            },
            { 
              title: "Name", 
              dataIndex: "leadId",
              render:(_,record) => (_.name)
            },
            { 
              title: "Source", 
              dataIndex: "leadId",
              render:(_,record) => (_.source)
            },
            { 
              title: "Phone", 
              dataIndex: "primaryPhone",
              ...getColumnSearchProps("primaryPhone")
            },
            {
              title: "Address",
              render: (_, record) => (
                `${record?.flat ?? ""} ${record?.area ??""} ${record?.city??""} ${record?.state??""} - ${record.pincode??""}`
              )
            },
            {
              title: "Actions",
              fixed: "right",
              width: 100,
              render: (_, record) => (
                <Space size="middle">
                  <Tooltip title="Edit">
                    <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                  </Tooltip>
                  {isAdmin && <Tooltip title="Delete">
                    <Button danger icon={<DeleteOutlined />} onClick={() => handleDelete(record._id)} />
                  </Tooltip>}
                  {/* <Tooltip title="Preview">
                    <Button icon={<EyeOutlined />} onClick={() => message.info(JSON.stringify(record, null, 2))} />
                  </Tooltip> */}
                </Space>
              ),
            },
          ]}
          pagination={{
            ...tableParams.pagination,
            responsive: true,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
          onChange={handleTableChange}
          scroll={{ x: 'max-content' }}
          size="small"
          className="w-full"
        />
      </Spin>

      <Drawer
        title={currentOrder ? "Edit Order" : "Add Order"}
        open={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setCurrentOrder(null);
          form.resetFields();
        }}
        width={600}
      >
        <Spin spinning={submitting}>
          <Form
            form={form}
            onFinish={handleFormSubmit}
            layout="vertical"
          >
            <div className="grid grid-cols-2 gap-4">
              <Form.Item
                name="amount"
                label="Amount"
                rules={[{ required: true, message: "Please input the amount!" }]}
              >
                <InputNumber
                  className="w-full"
                  formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/₹\s?|(,*)/g, '')}
                />
              </Form.Item>

              <Form.Item
                name="rate"
                label="Rate"
                rules={[{ required: true, message: "Please input the rate!" }]}
              >
                <InputNumber
                  className="w-full"
                  formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/₹\s?|(,*)/g, '')}
                />
              </Form.Item>

              <Form.Item
                name="otherCharges"
                label="Other Charges"
                rules={[{ required: true, message: "Please input other charges!" }]}
              >
                <InputNumber
                  className="w-full"
                  formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/₹\s?|(,*)/g, '')}
                />
              </Form.Item>

              <Form.Item
                name="orderValueIncludingGST"
                label="Final Amount (GST)"
                rules={[{ required: true, message: "Please input the final amount!" }]}
              >
                <InputNumber
                  className="w-full"
                  formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/₹\s?|(,*)/g, '')}
                />
              </Form.Item>

              <Form.Item
                name="quantity"
                label="Quantity"
                rules={[{ required: true, message: "Please input the quantity!" }]}
              >
                <InputNumber className="w-full" min={1} />
              </Form.Item>

              <Form.Item
                name="productCode"
                label="Product Code"
                rules={[{ required: true, message: "Please input the product code!" }]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="primaryPhone"
                label="Phone Number"
                rules={[
                  { required: true, message: "Please input the phone number!" },
                  { pattern: /^\+?[\d\s-]{10,}$/, message: "Please enter a valid phone number!" }
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="orderStatus"
                label="Status"
                rules={[{ required: true, message: "Please select the status!" }]}
              >
                <Select>
                  <Option value="CREATED">Created</Option>
                  <Option value="INPROGRSS">In Progress</Option>
                  <Option value="COMPLETED">Completed</Option>
                  <Option value="LOST">Lost</Option>
                </Select>
              </Form.Item>

              {isAdmin && (
                <Form.Item
                  name="poc"
                  label="Point of Contact"
                  rules={[{ required: true, message: "Please select a POC!" }]}
                >
                  <Select>
                    {systemUsers.map(user => (
                      <Option key={user._id} value={user._id}>
                        {user.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Form.Item
                name="pincode"
                label="Pincode"
              >
                <InputNumber className="w-full" />
              </Form.Item>

              <Form.Item
                name="flat"
                label="Flat/Building"
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="area"
                label="Area"
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="city"
                label="City"
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="state"
                label="State"
              >
                <Input />
              </Form.Item>
            </div>

            <Form.Item className="mt-4">
              <Button type="primary" htmlType="submit" loading={submitting} block>
                {currentOrder ? "Update Order" : "Create Order"}
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </Drawer>
    </div>
  );
};

export default OrderManagement;